// ManageSendEmail.tsx
import React, { useEffect, useMemo, useState } from "react";
import Popup from "../../../components/common/model/Popup";
import { DialogContent, DialogContentText, Grid } from "@mui/material";
import useStyles from "../../../components/common/model/styles";

import CustomButton from "../../../components/common/button/CustomButton";
import { sendSmsAllFields } from "./templateUtil";
import FormField from "../../../components/common/FormField";
import Notification from "../../../components/common/notification/Notification";
import Validation from "../../../utils/Validation";
import { useDispatch } from "react-redux";
import {
  sendEmailTemplateAction,
  setSendEmailTemplateAction,
} from "../../../store/reducers/templateSlice";
import { useSelector } from "react-redux";
import { getClientForEditAction } from "../../../store/reducers/adminClientsSlice";
import { setNotificationAction } from "../../../store/reducers/mmymSlice";
import dayjs from "dayjs";

interface ManageSendEmailProps {
  onHide: () => void;
  open: boolean;
  selectedSms: any;
  title?: string;
}

/* ------------------ helpers for placeholder replacement ------------------ */

function getNestedValue(obj: any, path: string) {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((acc: any, key: string) => {
    if (acc === undefined || acc === null) return undefined;
    const arrMatch = key.match(/^(.+)\[(\d+)\]$/);
    if (arrMatch) {
      const arrKey = arrMatch[1];
      const idx = parseInt(arrMatch[2], 10);
      if (!Array.isArray(acc[arrKey])) return undefined;
      return acc[arrKey][idx];
    }
    return acc[key];
  }, obj);
}

/** find value by key (case-insensitive) anywhere inside an object */
function findValueByKeyRecursive(obj: any, keyToFind: string): any {
  if (obj === null || obj === undefined) return undefined;
  const targetKey = keyToFind.toLowerCase();

  function helper(current: any): any {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== "object") return undefined;

    if (Array.isArray(current)) {
      for (const item of current) {
        const res = helper(item);
        if (res !== undefined) return res;
      }
      return undefined;
    }

    for (const k of Object.keys(current)) {
      if (k.toLowerCase() === targetKey) return current[k];
    }

    for (const k of Object.keys(current)) {
      const v = current[k];
      if (typeof v === "object") {
        const res = helper(v);
        if (res !== undefined) return res;
      }
    }
    return undefined;
  }

  return helper(obj);
}

/**
 * Replace placeholders like {{Key}} using mapping first, then fallback search in leadDetails.
 * Returns replaced result string.
 */
function replacePlaceholdersWithLeadData(
  template: string,
  leadDetails: any,
  mapping: Record<string, { path: string; format?: (value: any) => string }> = {},
  opts?: { removeUnknown?: boolean },
): { result: string; missing: string[] } {
  const removeUnknown = opts?.removeUnknown ?? true;
  const missing = new Set<string>();

  const result = (template || "").replace(
    /{{\s*([^}]+?)\s*}}/g,
    (_, rawKey) => {
      const key = rawKey.trim();
      const mappedConfig = mapping[key] ?? mapping[key.toLowerCase()];
      let value: any = undefined;

      if (mappedConfig) {
        value = getNestedValue(leadDetails, mappedConfig.path);
        if (value !== undefined && mappedConfig.format) {
          value = mappedConfig.format(value);
        }
      } else {
        // try common places
        value =
          (leadDetails?.lead && (leadDetails.lead as any)[key]) ??
          (leadDetails as any)[key];
        if (value === undefined) {
          value = findValueByKeyRecursive(leadDetails, key);
        }
      }

      if (value === undefined || value === null) {
        missing.add(key);
        return removeUnknown ? "" : `{{${key}}}`;
      }

      if (Array.isArray(value)) {
        return value
          .map((v) => (v === null || v === undefined ? "" : String(v)))
          .join(", ");
      }
      if (typeof value === "object") {
        try {
          return JSON.stringify(value);
        } catch {
          return String(value);
        }
      }
      return String(value);
    },
  );

  return { result, missing: Array.from(missing) };
}

/* ------------------ component ------------------ */

const ManageSendEmail: React.FC<ManageSendEmailProps> = ({
  onHide,
  open,
  selectedSms,
  title,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { isEmailTemplateSendSuccess } = useSelector(
    (state: any) => state.template,
  );
  const { leadDetails } = useSelector((state: any) => state.lead);
    const {userProfile} = useSelector((state: any) => state.user);
  const { editClientOriginalData } = useSelector(
    (state: any) => state.adminClients,
  );

  useEffect(() => {
    dispatch(getClientForEditAction());
    return () => {
      dispatch(setNotificationAction({ message: [], severity: "info" }));
    };
  }, [dispatch]);

  // default form state
  const [formData, setFormData] = useState<{ [k: string]: any }>({
    toMail: leadDetails?.lead?.customer_email ?? "",
    cc: [],
    bcc: [],
    subject: "",
    textBody: "emailTemplate",
    htmlBody: "",
    signature: "",
    fromEmail:"",
    signature_option: true,
    leadId: leadDetails?.lead?.generated_id ?? null,
  });

  // validation hook
  const [
    error,
    checkValidator,
    resetValidator,
    resetAllValidator,
    isError,
  ]: any = Validation({
    toMail: formData?.toMail,
    cc: formData?.cc,
    bcc: formData?.bcc,
    subject: formData?.subject,
  });

  // ---------- placeholder mapping: adjust as needed ----------
  const placeholderMapping: Record<
  string,
  {
    path: string;
    format?: (value: any) => string;
  }
> = {
    clientName: { path: "editClientOriginalData.contact_name" },
    clientAddress: { path: "editClientOriginalData.address" },
    clientEmail: { path: "editClientOriginalData.email" },
    clientWebsite: { path: "editClientOriginalData.website" },
    customerName: { path: "lead.customer_name" },
    customerEmail: { path: "lead.customer_email" },
    customerPhone: { path: "lead.customer_phone" },
     MoveDate: { path: "lead.moving_on_date", format: (value) => dayjs(value).format(userProfile?.dateFormat ?? "DD/MM/YYYY") },
    MoveTime: {path: "lead.moving_on_date", format: (value) => dayjs(value).format("hh:mm A")},
    clientId: { path: "lead.client_id" },
    createdAt: { path: "lead.created_at" },
    collectionAddressBeds: { path: "lead.collection_address[0].house_size" },
    SurveyorMobile: { path: "lead.surveyor_mobile" },
    collectionVolume: { path: "lead.collection_volume" },
    collectionAddress: { path: "lead.collection_address" },
    deliveryAddress: { path: "lead.delivery_address" },
    materialsTable: { path: "lead.materials_table" },
    materialIncluded: { path: "lead.material_included" },
    servicesTable: { path: "lead.services_table" },
    signature: { path: "lead.signature" },
    // below correct
    CName: { path: "lead.customer_name" },
    // UserName: { path: "lead.customer_name" },
    // ContactName: { path: "editClientOriginalData.contact_name" },
    SurveyorName: { path: "lead.surveyor_name" },
    SurveyType: { path: "lead.survey_type" },
    JobRef: { path: "lead.generated_id" },
    // EstimatePdf: { path: "lead.estimate_pdf" },
    CompanyName: { path: "editClientOriginalData.company_name" },
    CMobile1: { path: "lead.customer_phone" },
    CollectionAddress: { path: "lead.collection_address[0].address" },
    DeliveryAddress: { path: "lead.delivery_address[0].address" },
    JobAddress: { path: "lead.collection_address[0].address" },
    CompanyNumber: { path: "editClientOriginalData.phone_number" },
    Email: { path: "lead.customer_email" },
  };

  console.log("editClientOriginalData",editClientOriginalData);

   const senderEmailOptions = [
    userProfile?.email && {
    label: `${userProfile.email?.toLowerCase()}`,
    id: userProfile.email,
  },
  editClientOriginalData?.notification_email && editClientOriginalData?.is_notification_signature_confirmed && {
    label: `${editClientOriginalData.notification_email?.toLowerCase()}`,
    id: editClientOriginalData.notification_email,
  },
  editClientOriginalData?.accounts_email && editClientOriginalData?.is_accounts_email_signature_confirmed && {
    label: `${editClientOriginalData.accounts_email?.toLowerCase()}`,
    id: editClientOriginalData.accounts_email,
  },
  ];

  // Build replaced HTML body whenever selectedSms or leadDetails changes
  useEffect(() => {
    if (!selectedSms) return;

    const mergedData = {
  lead: leadDetails?.lead,
  editClientOriginalData,
};
    
    // compute replaced body
    const { result } = replacePlaceholdersWithLeadData(
      selectedSms?.body ?? "",
      mergedData ??  {},
      placeholderMapping,
      { removeUnknown: true }, // remove placeholders that have no value
    );

    const { result: replacedSubject } = replacePlaceholdersWithLeadData(
      selectedSms?.subject ?? "",
      mergedData ?? {},
      placeholderMapping,
      { removeUnknown: true },
    );

    // signature default from template (strip tags)
    const templateSignature =
      (selectedSms?.signature &&
        String(selectedSms.signature).replace(/(<([^>]+)>)/gi, "")) ||
      "";

    // set form data based on selected template and replaced HTML
    setFormData((prev) => ({
      ...prev,
      toMail: leadDetails?.lead?.customer_email ?? prev.toMail,
      subject: replacedSubject,
      htmlBody: result,
      signature: templateSignature,
      // Keep Add Signature enabled by default.
      signature_option: true,
    }));

    // reset validators for these fields
    resetAllValidator();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSms, leadDetails]);

  // Update toMail if leadDetails changes (e.g., loaded async)
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      toMail: leadDetails?.lead?.customer_email ?? prev.toMail,
    }));
  }, [leadDetails?.lead?.customer_email]);

  useEffect(() => {
    if (editClientOriginalData?.email_signature && formData?.signature === "") {
      setFormData((prev) => ({
        ...prev,
        signature: editClientOriginalData?.email_signature,
      }));
    }
  }, [editClientOriginalData?.email_signature]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement> | any) => {
    event?.preventDefault?.();
    checkValidator();
    // trigger send in effect below
    setIsActionCalled(true);
  };

  // handle checkboxes and normal inputs
  const onChangeAction = (event: any) => {
    const { id, value, type, checked, name } = event.target;
    const key = id || name;
    const newVal = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [key]: newVal,
    }));

    resetValidator(key);
  };

  // richtext editor change (CKEditor/Tiptap handler)
  const handleCKEditorChange = (data: string, id: string) => {
    setFormData((prev) => ({
      ...prev,
      [id]: data,
    }));
    resetValidator(id);
  };

  const onBlurAction = (e: any) => {
    const id = e?.target?.id;
    if (id) checkValidator(id);
  };

  // send action state
  const [isActionCalled, setIsActionCalled] = useState(false);

  useEffect(() => {
    if (!isActionCalled) return;
    if (!isError() && error !== null) {
      // Append signature if requested
      const updatedHtmlBody =
        formData.signature_option && formData.signature
          ? `${formData.htmlBody || ""}<br/>${formData.signature}`
          : formData.htmlBody || "";

      const { result: replacedSubject } = replacePlaceholdersWithLeadData(
        formData.subject ?? "",
        leadDetails ?? {},
        placeholderMapping,
        { removeUnknown: true },
      );

      const subjectPlainText = String(replacedSubject)
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .trim();

      const payload = {
        toMail: formData.toMail,
        subject: subjectPlainText,
        textBody: formData.textBody,
        htmlBody: updatedHtmlBody,
        cc: formData.cc,
        bcc: formData.bcc,
        fromEmail: formData.fromEmail,
        leadId: leadDetails?.lead?.generated_id ?? null,
      };

      dispatch(sendEmailTemplateAction(payload));
    } else {
      // show errors in console or notification
      console.warn("validation error", error);
    }
    setIsActionCalled(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActionCalled]);

  useEffect(() => {
    if (isEmailTemplateSendSuccess) {
      dispatch(setSendEmailTemplateAction(false));
      onHide();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmailTemplateSendSuccess]);

  const closeModel = () => {
    onHide();
  };

  // Inject senderEmailOptions into fromEmail field's options dynamically
  const updatedFields = sendSmsAllFields
    .map((field) => {
      if (field.id === "fromEmail") {
        return {
          ...field,
          options: senderEmailOptions.filter(Boolean),
        };
      }
      return field;
    })
    .filter((field) => {
      if (field.id === "signature" && !formData.signature_option) {
        return false;
      }
      return true;
    });

  return (
    <Popup
      title={selectedSms?.template_name ?? title}
      fullWidth
      maxWidth={"xl"}
      open={open}
      onHide={onHide}
      popupBg="#fff"
      footer={
        <>
          <CustomButton
            style={{ padding: "10px 30px", height: "36px" }}
            title="Send"
            onClick={handleSubmit}
          />
        </>
      }
    >
      <DialogContent>
        <DialogContentText className={classes.formAddBox}>
          <>
            <Notification />
            <Grid
              container
              direction="row"
              spacing={2}
              sx={{
                mt: 1,
                background: "#E5F4F1",
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #CFCFCF",
              }}
            >
              {updatedFields?.map((field: any) => (
                <FormField
                  key={field.id}
                  onPaste={(e: any) => e.preventDefault()}
                  onKeyDown={(event: any) => {
                    if (
                      [8, 9, 37, 38, 39, 40, 18, 17].includes(event.keyCode) ||
                      event.ctrlKey
                    )
                      return event;
                    if (
                      field?.patternAllowed &&
                      !field?.patternAllowed.test(event.key)
                    ) {
                      event.preventDefault();
                    } else if (
                      field?.patternAllowed &&
                      !field?.patternAllowed.test(
                        event.target.value + event.key,
                      )
                    ) {
                      event.preventDefault();
                    }
                  }}
                  {...{
                    ...field,
                    value: formData ? formData[field.id] : "",
                    checked:
                      field?.type === "checkbox"
                        ? Boolean(formData?.[field.id])
                        : undefined,
                    variant: "outlined",
                    inSingleGridForm: true,
                    onChange:
                      field?.type === "richtext"
                        ? (data: string) =>
                            handleCKEditorChange(data, field?.id)
                        : onChangeAction,
                    onBlur: onBlurAction,
                    isNonMarginTop: true,
                    InputProps: {
                      ...(field?.inputProps && field?.inputProps),
                      sx:
                        field.type === "text" || field.type === "password"
                          ? { height: "36px" }
                          : { padding: "10px 4px", height: "36px" },
                    },
                    error: error && error[field.id],
                  }}
                />
              ))}
            </Grid>
          </>
        </DialogContentText>
      </DialogContent>
    </Popup>
  );
};

export default ManageSendEmail;
